import { useState } from 'react';
import { Loader2, Pause, Play, SkipForward, X } from 'lucide-react';
import { CONDITIONS, tracksFor, type RuqyahCondition } from '@/data/ruqyahAudio';
import { haptic } from '@/components/ui';
import { useRuqyah, type QueueTrack } from './ruqyahPlayer';

export const clock = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};
const initials = (name: string) => name.replace(/[ʿʾ]/g, '').split(/\s+/).filter((w) => !/^(al|ar|as|ash|an|ad|aṣ|ibn|bin)-?$/i.test(w)).map((w) => w.replace(/^(al|ar|as|ash|an|ad|aṣ)-/i, '')[0]).slice(0, 2).join('').toUpperCase();

/** Listen tab: pick a condition, then play one recording or all of them back to back. */
export function RuqyahListen() {
  const [cond, setCond] = useState<RuqyahCondition>('eye');
  const tracks = tracksFor(cond);
  const r = useRuqyah();
  const current = r.status !== 'idle' ? r.queue[r.index] : null;
  const info = CONDITIONS.find((c) => c.id === cond)!;
  const total = tracks.reduce((a, t) => a + t.seconds, 0);
  return (
    <>
      <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
        {CONDITIONS.map((c) => (
          <button key={c.id} type="button" aria-pressed={cond === c.id} onClick={() => setCond(c.id)}
            className={`press shrink-0 rounded-full px-4 py-2 text-[14px] font-semibold ${cond === c.id ? 'bg-accent text-accent-ink' : 'bg-surface'}`}>
            {c.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3 rounded-[22px] bg-surface p-4">
        <div className="min-w-0 flex-1">
          <div className="text-[17px] font-bold">{info.label} <span className="font-kufi text-[15px] font-normal text-gold">{info.ar}</span></div>
          <div className="text-[13px] text-muted">{info.blurb} {tracks.length} recordings · {clock(total)}</div>
        </div>
        <button type="button" className="press flex shrink-0 items-center gap-1.5 rounded-full bg-accent px-4 py-2.5 text-[15px] font-semibold text-accent-ink"
          onClick={() => { haptic(); r.playQueue(tracks, 0); }}><Play size={16} /> Play all</button>
      </div>
      <div className="overflow-hidden rounded-[22px] bg-surface">
        {tracks.map((t, i) => {
          const on = current?.id === t.id;
          return (
            <button key={t.id} type="button" aria-label={`Play ${t.title} — ${t.reciter}`}
              onClick={() => { haptic(); if (on) r.toggle(); else r.playQueue(tracks, i); }}
              className={`press flex w-full items-center gap-3 border-b border-hairline px-4 py-3 text-left last:border-0 ${on ? 'bg-accent/10' : ''}`}>
              <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full text-[13px] font-bold ${on ? 'bg-accent text-accent-ink' : 'bg-fill text-muted'}`}>
                {on ? (r.status === 'loading' ? <Loader2 size={16} className="animate-spin" /> : r.status === 'playing' ? <Pause size={16} /> : <Play size={16} />) : initials(t.reciter)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-semibold">{t.reciter} <span className="font-arabic text-[14px] font-normal text-muted">{t.reciterAr}</span></span>
                <span className="block truncate text-[12px] text-muted">{t.title} · {clock(t.seconds)}</span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="px-1 text-[12px] leading-relaxed text-faint">Recordings: Internet Archive. "Play all" carries on to the next recording, also with the screen locked.</p>
    </>
  );
}

/** Now-playing bar for ruqyah, shown on every screen (same spot as the Qurʾān mini-player — only one plays at a time). */
export function RuqyahBar() {
  const r = useRuqyah();
  const t: QueueTrack | undefined = r.queue[r.index];
  if (r.status === 'idle' || !t) return null;
  const pct = r.durationMs ? Math.min(1, r.positionMs / r.durationMs) : 0;
  return (
    <div className="glass fixed inset-x-2 z-40 overflow-hidden rounded-[22px] sm:left-1/2 sm:right-auto sm:w-[560px] sm:-translate-x-1/2"
      style={{ bottom: 'calc(var(--tabbar-h) + var(--safe-bottom) + 14px)' }} role="region" aria-label={`${r.album} player`}>
      <div className="flex items-center gap-3 py-2 pl-4 pr-2">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-semibold">{t.title}</span>
          <span className="block truncate text-[13px] text-muted">
            {r.status === 'error' ? r.error : `${t.reciter}${r.durationMs || t.seconds ? ` · ${clock(r.positionMs / 1000)} / ${clock((r.durationMs || t.seconds * 1000) / 1000)}` : ''}${r.queue.length > 1 ? ` · ${r.index + 1} of ${r.queue.length}` : ''}`}
          </span>
        </span>
        <button type="button" aria-label={r.status === 'playing' ? 'Pause ruqyah' : 'Play ruqyah'} className="press grid h-11 w-11 place-items-center rounded-full bg-accent text-accent-ink" onClick={() => { haptic(); r.toggle(); }}>
          {r.status === 'loading' ? <Loader2 size={20} className="animate-spin" /> : r.status === 'playing' ? <Pause size={20} /> : <Play size={20} />}
        </button>
        {r.index + 1 < r.queue.length && <button type="button" aria-label="Next recording" className="press grid h-9 w-9 place-items-center rounded-full text-muted" onClick={() => r.next()}><SkipForward size={18} /></button>}
        <button type="button" aria-label="Stop ruqyah" className="press grid h-9 w-9 place-items-center rounded-full text-muted" onClick={() => r.stop()}><X size={18} /></button>
      </div>
      <div className="h-0.5 bg-fill"><div className="h-full bg-accent" style={{ width: `${pct * 100}%` }} /></div>
    </div>
  );
}
